import { ControlledInput, ControlledRadioCardLegendGroup, ControlledSwitch } from '@@viewCommonComponents/controlled';
import { Card, Collapse, ReimbursementChart, SectionTitle, TotalTable } from '@fasstech/middle-design-system';
import { isGuaranteeInvalRequired } from '@@viewHelpers/validations/guaranteesPrevTns.js';
import mapOfferNodeLegendOptions from '@@viewHelpers/mapOfferNodeLegendOptions';
import useNiveauxGarantiesTarifsPrevTnsInvaliditeForm from '../hooks/useNiveauxGarantiesTarifsPrevTnsInvaliditeForm';
import { numberIsBetween } from '@@viewHelpers/validations/index.js';
import { formatMultiline } from '@@viewHelpers/format.jsx';
import getExtraInvalMessageInfo from '../extra/getExtraInvalMessageInfo.js';
import extraMadelin from '../extra/extraMadelin.js';
import { propOr } from 'ramda';

const NiveauxGarantiesTarifsPrevTnsInvaliditeForm = ({
  form,
  guarantees,
  getMiddleText,
  getMiddleNumber,
  getMiddleBoolean,
  customerCSPCategory,
  onInvalDepCapFocus,
  onInvalDepCapBlur,
  annualIncome
}) => {
  const {
    garInval,
    garInvalOptionDep,
    OPTION_INVAL,
    INVAL_MAD,
    OPTION_INVAL_DEP,
    invaliditeCotisation,
    invalHealthInsurances,
    invalReimbursementGraphContent
  } = useNiveauxGarantiesTarifsPrevTnsInvaliditeForm({
    form,
    guarantees,
    getMiddleText,
    annualIncome
  });

  return (
    <Collapse title={getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INVAL_LABEL')}>
      <div className="flex gap-10 flex-col-reverse md:flex-row">
        {/*Left column*/}
        <div className="flex-1">
          {OPTION_INVAL && (
            <ControlledSwitch
              name="guarantees.OPTION_INVAL.selected"
              label={getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INVAL_CHOIX_LABEL')}
              labelClassNames="text-lg"
              displayError={false}
              disabled={!OPTION_INVAL.editable}
              form={form}
              recommended={OPTION_INVAL.recommended}
              infoMessage={formatMultiline(
                getExtraInvalMessageInfo(
                  getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INVAL_MESSAGE_INFO'),
                  customerCSPCategory
                )
              )}
              data-testid="OPTION_INVAL"
            />
          )}

          {garInval && (
            <>
              <hr className="mt-8 mb-6"/>
              <div className="flex">
                <div className="w-2/3 flex flex-row">
                  <SectionTitle>
                    {getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INVAL_CAPITAL_LABEL')}
                  </SectionTitle>

                  <div className="mb-4">
                    <ControlledRadioCardLegendGroup
                      legends={mapOfferNodeLegendOptions(guarantees.INVAL_CAP).legends}
                      options={mapOfferNodeLegendOptions(guarantees.INVAL_CAP).options}
                      form={form}
                      name="guarantees.INVAL_CAP.value"
                      rules={isGuaranteeInvalRequired}
                      data-testid="INVAL_CAP"
                    />
                  </div>
                </div>
                <div className="w-1/3">

                  <ReimbursementChart
                    healthInsurances={invalHealthInsurances}
                    labels={propOr([], 'labelsRO', OPTION_INVAL)}
                    ROs={propOr([], 'RO', OPTION_INVAL)}
                    displayGraph
                    rows={invalReimbursementGraphContent.rows}
                    note={invalReimbursementGraphContent.note}
                  />
                </div>

              </div>

              {INVAL_MAD && (
                <ControlledSwitch
                  extraClick={() => extraMadelin(INVAL_MAD.selected, form)}
                  containerClassName="mb-8"
                  label={getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INVAL_FISCA_MADELIN_LABEL')}
                  labelClassNames="text-lg"
                  form={form}
                  name="guarantees.INVAL_MAD.selected"
                  displayError={false}
                  disabled={!INVAL_MAD.editable}
                  data-testid="INVAL_MAD"
                />
              )}

              <hr className="mb-4"></hr>

              {getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_INVAL_OPTION_EST_NECESSAIRE') && (
                <>
                  <SectionTitle>
                    {getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INVAL_OPTION_LABEL')}
                  </SectionTitle>
                  {OPTION_INVAL_DEP &&
                    getMiddleBoolean(
                      'NIVEAUXGARANTIESETTARIFS_GAR_INVAL_OPTION_DEPENDANCE_EST_NECESSAIRE'
                    ) && (
                    <>
                      <ControlledSwitch
                        name="guarantees.OPTION_INVAL_DEP.selected"
                        label={getMiddleText(
                          'NIVEAUXGARANTIESETTARIFS_GAR_INVAL_OPTION_DEPENDANCE_LABEL'
                        )}
                        labelClassNames="text-lg"
                        displayError={false}
                        disabled={!OPTION_INVAL_DEP.editable}
                        form={form}
                        recommended={OPTION_INVAL_DEP.recommended}
                        infoMessage={getMiddleText(
                          'NIVEAUXGARANTIESETTARIFS_GAR_INVAL_OPTION_DEPENDANCE_MESSAGE_INFO'
                        )}
                        data-testid="OPTION_INVAL_DEP"
                      />

                      {garInvalOptionDep && (
                        <div className="w-full md:w-1/4! mb-8" onFocus={onInvalDepCapFocus}
                          onBlur={onInvalDepCapBlur}>
                          <Card className="pr-10!" variant="outline">
                            <ControlledInput
                              name="guarantees.INVAL_DEP_CAP.value"
                              form={form}
                              label={getMiddleText(
                                'NIVEAUXGARANTIESETTARIFS_GAR_INVAL_OPTION_DEPENDANCE_CAPITAL_LABEL'
                              )}
                              variant="number"
                              rules={
                                garInvalOptionDep &&
                                  numberIsBetween(
                                    getMiddleNumber(
                                      'NIVEAUXGARANTIESETTARIFS_GAR_INVAL_OPTION_DEPENDANCE_VALEUR_MIN'
                                    ),
                                    getMiddleNumber(
                                      'NIVEAUXGARANTIESETTARIFS_GAR_INVAL_OPTION_DEPENDANCE_VALEUR_MAX'
                                    ),
                                    getMiddleText(
                                      'NIVEAUXGARANTIESETTARIFS_GAR_INVAL_OPTION_DEPENDANCE_MESSAGE_ERREUR',
                                      {
                                        minCapital: getMiddleNumber(
                                          'NIVEAUXGARANTIESETTARIFS_GAR_INVAL_OPTION_DEPENDANCE_VALEUR_MIN'
                                        ),
                                        maxCapital: getMiddleNumber(
                                          'NIVEAUXGARANTIESETTARIFS_GAR_INVAL_OPTION_DEPENDANCE_VALEUR_MAX'
                                        )
                                      }
                                    ),
                                    getMiddleBoolean(
                                      'NIVEAUXGARANTIESETTARIFS_GAR_INVAL_OPTION_DEPENDANCE_EST_OBLIGATOIRE'
                                    )
                                  )
                              }
                              append="€"
                              data-testid="INVAL_DEP_CAP"
                            />
                          </Card>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              <TotalTable rows={invaliditeCotisation}/>
            </>
          )}
        </div>
      </div>
    </Collapse>
  );
};

export default NiveauxGarantiesTarifsPrevTnsInvaliditeForm;
